
import { Injectable } from '@angular/core';
import { 
  context, 
  trace, 
  SpanStatusCode, 
  SpanKind, 
  Span, 
  Context,
  ROOT_CONTEXT, 
  propagation 
} from '@opentelemetry/api';
import { TelemetryService } from './telemetry/telemetry.service';
import { LoggingService } from './logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class TracingService {
  private rootSpan: Span | null = null;
  private activeSpans: Map<string, Span> = new Map();
  private tracer: any;
  private readonly isMobileDevice: boolean;
  private activeTraceId: string | null = null;

  constructor(
    private telemetryService: TelemetryService,
    private logger: LoggingService
  ) {
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // Get tracer from the service name in TelemetryService
    this.tracer = trace.getTracer(this.telemetryService.getServiceName());
    this.initializeRootSpan();
  }

  private initializeRootSpan(): void {
    this.rootSpan = this.tracer.startSpan('application.lifecycle', {
      kind: SpanKind.INTERNAL,
      attributes: {
        'app.name': this.telemetryService.getServiceName(),
        'app.version': '1.0',
        'device.type': this.isMobileDevice ? 'mobile' : 'desktop',
        'device.userAgent': navigator.userAgent
      }
    });
    
    // Only proceed with context.with if rootSpan is not null
    if (this.rootSpan) {
      context.with(trace.setSpan(context.active(), this.rootSpan), () => {
        const spanContext = this.rootSpan?.spanContext();
        if (spanContext) {
          this.activeTraceId = spanContext.traceId;
          this.logger.info('TracingService', `Root trace initialized with ID: ${this.activeTraceId}`);
        }
      });
    }
    
    window.addEventListener('beforeunload', () => {
      this.endRootSpan();
    });
  }

  private endRootSpan(): void {
    if (this.rootSpan) {
      this.logger.info('TracingService', 'Ending root application span');
      this.rootSpan.end();
      this.rootSpan = null;
    }
  }

  public startSpan(name: string, attributes: Record<string, any> = {}, parentSpanName?: string): string {
    try {
      const spanAttributes = {
        ...attributes,
        'device.type': this.isMobileDevice ? 'mobile' : 'desktop'
      };
      let parentSpan: Span | undefined;
      let spanContext: Context = context.active();
      if (parentSpanName && this.activeSpans.has(parentSpanName)) {
        parentSpan = this.activeSpans.get(parentSpanName)!;
        spanContext = trace.setSpan(ROOT_CONTEXT, parentSpan);
      }
      const span = this.tracer.startSpan(name, {
        kind: SpanKind.INTERNAL,
        attributes: spanAttributes
      }, spanContext);
      const spanId = `${name}-${Date.now()}`;
      this.activeSpans.set(spanId, span);
      this.logger.debug('TracingService', `Started span: ${name} (${spanId})`, attributes);
      return spanId;
    } catch (error) {
      this.logger.error('TracingService', `Error starting span ${name}`, error);
      return '';
    }
  }

  public addSpanEvent(spanId: string, name: string, attributes: Record<string, any> = {}): void {
    try {
      const span = this.activeSpans.get(spanId);
      if (span) {
        span.addEvent(name, attributes);
        this.logger.debug('TracingService', `Added event ${name} to span ${spanId}`, attributes);
      } else {
        this.logger.warn('TracingService', `Span not found for event: ${spanId}`, { name, attributes });
      }
    } catch (error) {
      this.logger.error('TracingService', `Error adding event to span ${spanId}`, error);
    }
  }

  public endSpan(spanId: string, status?: { code: SpanStatusCode, message?: string }): void {
    try {
      const span = this.activeSpans.get(spanId);
      if (span) {
        if (status) {
          span.setStatus(status);
        }
        span.end();
        this.activeSpans.delete(spanId);
        this.logger.debug('TracingService', `Ended span: ${spanId}`);
      } else {
        this.logger.warn('TracingService', `Span not found for ending: ${spanId}`);
      }
    } catch (error) {
      this.logger.error('TracingService', `Error ending span ${spanId}`, error);
    }
  }

  public async trace<T>(name: string, fn: () => Promise<T>, attributes: Record<string, any> = {}): Promise<T> {
    const spanId = this.startSpan(name, attributes);
    try {
      const result = await fn();
      this.endSpan(spanId, { code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      this.endSpan(spanId, { 
        code: SpanStatusCode.ERROR, 
        message: error instanceof Error ? error.message : String(error) 
      });
      throw error;
    }
  }

  public getActiveTraceId(): string | null {
    return this.activeTraceId;
  }

  public createPageLoadTrace(): void {
    const pageLoadSpanId = this.startSpan('page.load', {
      'page.url': window.location.href,
      'page.referer': document.referrer
    });
    const timing = performance.timing;
    const navigationStart = timing.navigationStart;
    const milestones = [
      { name: 'dns', start: timing.domainLookupStart, end: timing.domainLookupEnd },
      { name: 'tcp', start: timing.connectStart, end: timing.connectEnd },
      { name: 'request', start: timing.requestStart, end: timing.responseStart },
      { name: 'response', start: timing.responseStart, end: timing.responseEnd },
      { name: 'dom', start: timing.domLoading, end: timing.domComplete },
      { name: 'load', start: timing.loadEventStart, end: timing.loadEventEnd }
    ];
    milestones.forEach(milestone => {
      if (milestone.start > 0 && milestone.end > 0) {
        this.addSpanEvent(pageLoadSpanId, `page.${milestone.name}`, {
          [`${milestone.name}.start`]: milestone.start - navigationStart,
          [`${milestone.name}.end`]: milestone.end - navigationStart,
          [`${milestone.name}.duration`]: milestone.end - milestone.start
        });
      }
    });
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    if (firstPaint) {
      this.addSpanEvent(pageLoadSpanId, 'page.first-paint', {
        'firstPaint.time': firstPaint.startTime
      });
    }
    this.endSpan(pageLoadSpanId, { code: SpanStatusCode.OK });
  }

  public createHttpRequestTrace(method: string, url: string): { spanId: string, headers: Record<string, string> } {
    const spanId = this.startSpan(`http.request.${method.toLowerCase()}`, {
      'http.method': method,
      'http.url': url
    });
    const span = this.activeSpans.get(spanId);
    const headers: Record<string, string> = {};
    if (span) {
      const ctx = trace.setSpan(context.active(), span);
      propagation.inject(ctx, headers);
    }
    return { spanId, headers };
  }
}